// @ts-nocheck
import React, { useMemo } from "react";
import { Chart as ChartJS, registerables } from "chart.js";
import { Line } from "react-chartjs-2";
import "chartjs-adapter-date-fns";
import type { PreviewData } from "../../types/composedForecast";
import { OVERLAY_COLORS } from "../../utils/colorUtils";

ChartJS.register(...registerables);

interface DataSourcePreviewChartProps {
  data: PreviewData;
}

// TODO: Allow users to choose chart type (bar, scatter, etc.) in future iteration
const DataSourcePreviewChart: React.FC<DataSourcePreviewChartProps> = ({ data }) => {
  const isMultiSeries = !!(data.series && data.series.length > 0);

  const chartData = useMemo(() => {
    if (isMultiSeries) {
      return {
        datasets: data.series!.map((s, idx) => ({
          label: s.label,
          data: s.timestamps.map((ts, i) => ({ x: ts, y: s.values[i] })),
          borderColor: s.color ?? OVERLAY_COLORS[idx % OVERLAY_COLORS.length],
          backgroundColor: "transparent",
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.1,
          fill: false,
        })),
      };
    }

    return {
      datasets: [
        {
          label: data.label + (data.unit ? ` (${data.unit})` : ""),
          data: data.timestamps.map((ts, i) => ({
            x: ts,
            y: data.values[i],
          })),
          borderColor: "rgb(59, 130, 246)",
          backgroundColor: "rgba(59, 130, 246, 0.1)",
          borderWidth: 1.5,
          pointRadius: 0,
          pointHoverRadius: 3,
          tension: 0.1,
          fill: true,
        },
      ],
    };
  }, [data, isMultiSeries]);

  const options = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index" as const,
      intersect: false,
    },
    plugins: {
      legend: { display: isMultiSeries && (data.series?.length ?? 0) > 1 },
      tooltip: {
        callbacks: {
          title: (items: any) => {
            if (!items[0]) return "";
            return new Date(items[0].parsed.x).toLocaleString();
          },
        },
      },
    },
    scales: {
      x: {
        type: "time" as const,
        grid: { display: false },
        ticks: { font: { size: 10 }, maxTicksLimit: 6 },
      },
      y: {
        grid: { color: "rgba(0,0,0,0.05)" },
        ticks: { font: { size: 10 } },
        title: data.unit
          ? { display: true, text: data.unit, font: { size: 10 } }
          : undefined,
      },
    },
  }), [data.unit, isMultiSeries, data.series?.length]);

  return (
    <div>
      <div className="h-40 w-full">
        <Line data={chartData} options={options} />
      </div>
      {data.warnings && data.warnings.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {data.warnings.map((w, i) => (
            <p key={i} className="font-inter text-eai-body-xs text-eai-warning-text">{w}</p>
          ))}
        </div>
      )}
    </div>
  );
};

export default DataSourcePreviewChart;
